import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { createProfileReportFinished } from '@capital-access/common/actions';
import {
  getUserSettingsCulture,
  getUserSettingsCurrency,
  getUserSettingsLanguage,
  getUserSettingsTimezone
} from '@capital-access/common/globalization';
import { LocalizationService } from '@capital-access/common/localization';
import {
  getSelectedRelatedSecurities,
  getSelectedSecurity,
  getSelectedSecuritySurveillancePermission
} from '@capital-access/common/security';
import { ModuleName } from '@capital-access/common/utils';
import { NotificationService } from '@capital-access/firefly/components';
import { getDefaultSecurityListSettings } from '@capital-access/lists/common';
import { CompositeId, ProfileId, ProfileType } from '@capital-access/profiles/common';
import {
  createConfigurableProfileReport,
  MAX_REPORT_PROFILES_COUNT,
  MAX_SMALL_REPORT_ITEMS_COUNT,
  ReportType
} from '@capital-access/reporting/common';
import { LOCALIZATION_KEY } from '../../constants';
import { generateReport } from '../../core/+state/report.actions';
import { OwnershipSource } from '../../core/models/ownership-source.enum';
import { ProfileReportNamingService } from '../../profile/services/profile-report-naming.service';
import { ConfigurableProfileReportRequest } from '../models/configurable-profile-report.models';

@Injectable()
export class ConfigurableProfileReportEffects {
  createConfigurableProfileReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createConfigurableProfileReport),
      withLatestFrom(
        this.store.select(getSelectedSecurity),
        this.store.select(getSelectedRelatedSecurities),
        this.store.select(getUserSettingsTimezone),
        this.store.select(getUserSettingsCulture),
        this.store.select(getUserSettingsLanguage),
        this.store.select(getDefaultSecurityListSettings),
        this.store.select(getSelectedSecuritySurveillancePermission),
        this.store.select(getUserSettingsCurrency)
      ),
      mergeMap(
        ([
          request,
          security,
          relatedSecurities,
          timeZone,
          culture,
          language,
          defaultPeerList,
          isSrvPermissioned,
          currency
        ]) => {
          const exportReportModuleName = request.moduleName as ModuleName;
          const includedContacts = request.includedContacts ?? [];
          const profiles = request.profiles.map(
            p => new ProfileId(new CompositeId(p.prId, p.crmId), request.profileType as ProfileType, p.name)
          );
          const isSrv = security?.surveillance ?? false;
          const reportRequest = this.profileReportNameService
            .getProfileReportFileName(
              isSrvPermissioned,
              isSrv,
              { profiles, profileType: request.profileType as ProfileType },
              timeZone
            )
            .pipe(
              map(fileName => {
                return this.generateProfileReportRequest(
                  {
                    profileIds: profiles.map(p => p.id.toString()),
                    reportType:
                      request.profileType === 'contact' ? ReportType.ContactTemplate : ReportType.InstitutionTemplate,
                    timeZone,
                    culture,
                    language,
                    securityId: security?.id,
                    defaultPeerListId: defaultPeerList.id!,
                    fileName,
                    currency,
                    combinedSecurityIds: relatedSecurities.map(x => x.id),
                    dataSource: isSrv ? OwnershipSource.Surveillance : OwnershipSource.Public,
                    activityHistoryFilters: request.activityHistoryFilters,
                    comments: request.comments,
                    templateId: request.templateId,
                    includedContacts,
                    moduleName: exportReportModuleName,
                    reportedQuartersFilter: request.reportedQuartersFilter,
                    outputFormat: request.outputFormat
                  },
                  request.isExportLocalization
                );
              })
            );
          // Custom toast logic for large exports
          const isLargeExport = request.profiles.length > MAX_SMALL_REPORT_ITEMS_COUNT;
          const min = isLargeExport
            ? request.profiles.length <= MAX_REPORT_PROFILES_COUNT
              ? 'two'
              : 'three'
            : undefined;
          const localizationKey = isLargeExport
            ? 'exportCreationStarted'
            : request.isReportingExport
            ? 'exportStarted'
            : 'createStarted';
          const localizationParams = isLargeExport && min ? { min } : {};
          return this.localization.localize(localizationKey, localizationParams, { scope: LOCALIZATION_KEY }).pipe(
            tap(message => this.notificationService.notify(message, { type: 'info' })),
            switchMap(() => reportRequest)
          );
        }
      )
    )
  );

  private generateProfileReportRequest(request: ConfigurableProfileReportRequest, isExportLocalization = false) {
    const profileType = request.reportType === ReportType.InstitutionTemplate ? 'Institution' : 'Contact';
    let successMessage;
    let failureMessage;
    if (isExportLocalization) {
      successMessage = `configurable${profileType}Profile${request.profileIds.length > 1 ? 's' : ''}Created`;
      failureMessage = `configurable${profileType}InvestorProfile${request.profileIds.length > 1 ? 's' : ''}Failed`;
    } else {
      const profileTypeLowerCase = profileType.toLowerCase();
      successMessage = `${profileTypeLowerCase}Profile${request.profileIds.length > 1 ? 's' : ''}Created`;
      failureMessage = `${profileTypeLowerCase}InvestorProfile${request.profileIds.length > 1 ? 's' : ''}Failed`;
    }

    return generateReport({
      request,
      url: 'configurable-reporting/process/batch/launch',
      successMessage,
      failureMessage,
      finishAction: createProfileReportFinished()
    });
  }

  constructor(
    private actions$: Actions,
    private store: Store,
    private localization: LocalizationService,
    private notificationService: NotificationService,
    private profileReportNameService: ProfileReportNamingService
  ) {}
}
